import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CopyIcon } from "@/components/icons";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const StepDot = ({ n, active }) => (
  <div
    className={`w-8 h-8 shrink-0 rounded-full box-border flex items-center justify-center text-sm font-semibold ${
      active ? "bg-accent border border-foreground" : "border border-[#B9B2A0] text-muted-foreground"
    }`}
  >
    {n}
  </div>
);

const StepCard = ({ n, active, title, children, className = "" }) => (
  <div className={`paper rounded-2xl border border-border p-6 lg:p-7 flex flex-col gap-4 ${className}`}>
    <div className="flex gap-3.5 items-center">
      <StepDot n={n} active={active} />
      <span className="text-lg font-semibold">{title}</span>
    </div>
    {children}
  </div>
);

const mask = (key) => (key.length > 8 ? `${key.slice(0, 3)}••••••••${key.slice(-4)}` : key);

export function InstallPage() {
  const [key, setKey] = useState(null);
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    api.getWidgetKey().then((d) => setKey(d.widget_key)).catch(() => {});
    api.getWidgetStatus().then(setStatus).catch(() => {});
  }, []);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const s = await api.getWidgetStatus();
      setStatus(s);
      if (!s.installed) toast("No visitor messages yet");
    } catch (_) {
      toast.error("Could not check the widget");
    } finally {
      setChecking(false);
    }
  }, []);

  const src = `${BACKEND}/api/widget.js`;
  const snippet = key ? `<script src="${src}" data-firstrun-key="${key}" defer></script>` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
    } catch (_) {
      const ta = document.createElement("textarea");
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success("Embed snippet copied");
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const d = await api.regenerateWidgetKey();
      setKey(d.widget_key);
      toast.success("New key generated. Update the snippet on your site.");
    } catch (_) {
      toast.error("Could not regenerate key");
    } finally {
      setRegenerating(false);
    }
  };

  const installed = !!status?.installed;

  return (
    <div data-testid="embed-panel" className="flex flex-col gap-7 fade-up">
      <div className="flex flex-col gap-1.5">
        <h1 className="font-heading text-[40px] lg:text-5xl leading-none tracking-[-0.02em]">Install</h1>
        <p className="text-base text-soft">Add the assistant to any site in three quick steps.</p>
      </div>

      <div className="grid gap-6 lg:gap-8 lg:grid-cols-2">
        <div className="flex flex-col gap-6">
          <StepCard n={1} active title="Copy your script tag">
            {key ? (
              <>
                <pre
                  data-testid="embed-code-snippet"
                  className="paper-dark rounded-xl px-6 py-5 text-primary-foreground font-mono text-[13px] leading-[1.7] whitespace-pre-wrap break-all"
                >
                  <span className="text-accent">&lt;script</span> src="{src}"{"\n  "}data-firstrun-key="{key}"
                  <span className="text-accent"> defer&gt;&lt;/script&gt;</span>
                </pre>
                <button
                  data-testid="copy-embed-button"
                  onClick={copy}
                  className="paper-dark self-start h-11 px-[18px] flex items-center gap-2.5 rounded-xl text-primary-foreground text-[15px] font-medium"
                >
                  <CopyIcon size={16} /> Copy snippet
                </button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Loading your key…</p>
            )}
          </StepCard>

          <StepCard n={2} title="Paste it before the closing body tag">
            <p className="text-[15px] leading-[1.55] text-soft">
              Add it once, in your site template. The widget loads on every page that uses it.
            </p>
          </StepCard>

          <StepCard n={3} title="Confirm it works">
            <div
              data-testid="widget-status"
              className={`flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl ${
                installed ? "bg-success/10" : "bg-[#FFF1D6]"
              }`}
            >
              <span className={`flex items-center gap-2.5 text-[15px] ${installed ? "text-success" : "text-[#6B3A00]"}`}>
                <span className={`w-2 h-2 rounded-full ${installed ? "bg-success" : "bg-[#B45309]"}`} />
                {installed
                  ? `Live. Visitors have sent ${status.messages} message${status.messages === 1 ? "" : "s"}.`
                  : "Waiting for the first message"}
              </span>
              <button
                onClick={check}
                disabled={checking}
                className="h-[38px] px-4 shrink-0 rounded-[10px] border border-foreground text-sm font-medium disabled:opacity-50"
              >
                {checking ? "Checking…" : "Check now"}
              </button>
            </div>
          </StepCard>

          <div className="paper rounded-2xl border border-border px-6 lg:px-7 py-5 flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-[15px] font-semibold">Widget key</span>
              <span className="font-mono text-[13px] text-muted-foreground truncate">{key ? mask(key) : "…"}</span>
            </div>
            <button
              data-testid="regenerate-key-button"
              onClick={regenerate}
              disabled={regenerating}
              className="h-10 px-4 shrink-0 rounded-[10px] border border-input text-sm font-medium disabled:opacity-50"
            >
              {regenerating ? "Regenerating…" : "Regenerate"}
            </button>
          </div>
        </div>

        {/* Live preview (illustrative) */}
        <div className="paper rounded-2xl border border-border relative overflow-hidden flex flex-col min-h-[520px] lg:min-h-[640px]">
          <div className="h-[52px] shrink-0 px-6 flex items-center justify-between border-b border-border">
            <span className="text-base font-semibold">Live preview</span>
            <span className="font-mono text-xs text-muted-foreground">yourapp.com</span>
          </div>
          <div className="flex-1 p-8 flex flex-col gap-3.5" aria-hidden="true">
            <div className="w-60 h-6 rounded-md bg-track" />
            <div className="w-full h-3 rounded-md bg-[#EAE5D7]" />
            <div className="w-[90%] h-3 rounded-md bg-[#EAE5D7]" />
            <div className="w-[76%] h-3 rounded-md bg-[#EAE5D7]" />
            <div className="h-[120px] rounded-xl bg-[#EAE5D7] mt-3" />
          </div>
          <div className="absolute right-6 bottom-6 w-[calc(100%-3rem)] max-w-[340px] rounded-[18px] bg-white border border-foreground shadow-[6px_6px_0_hsl(var(--primary))] overflow-hidden">
            <div className="h-[52px] px-[18px] flex items-center gap-2.5 border-b border-border">
              <div className="w-2.5 h-2.5 rounded-full bg-success" />
              <span className="font-semibold text-[15px]">Docs assistant</span>
            </div>
            <div className="p-[18px]">
              <div className="paper-inset px-3.5 py-3 rounded-[14px_14px_14px_4px] text-sm leading-normal">
                Hi! Ask me anything about the product and I will point you to the right page.
              </div>
            </div>
            <div className="mx-[18px] mb-[18px] h-11 rounded-xl border border-border flex items-center px-3.5 text-sm text-muted-foreground">
              Ask a question
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
